/*
 *  ------------------------------------------------------------
 *  Centralised Firebase-Auth helper that:
 *    • Exposes the signed-in firebase.User via user$ (BehaviorSubject)
 *    • Publishes isLoggedIn and accessToken convenience getters
 *    • Works with AngularFire v7+ (compat namespace)
 *  ------------------------------------------------------------ */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env';
import { AngularFireAuth } from '@angular/fire/compat/auth'; // AngularFire compat API
import firebase from 'firebase/compat/app'; // firebase.User typings
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class AuthService {
  /* ------------------------------------------------------------------ */
  /*  Reactive user stream                                              */
  /* ------------------------------------------------------------------ */
  private readonly _user$ = new BehaviorSubject<firebase.User | null>(null);
  /** Public observable for components/pipes */
  readonly user$ = this._user$.asObservable();

  /* ------------------------------------------------------------------ */
  /*  Simple flags / tokens                                             */
  /* ------------------------------------------------------------------ */
  private _isLoggedIn = false;
  private _accessToken: string | null = null;

  constructor(private afAuth: AngularFireAuth, private router: Router, private toastr: ToastrService) {
    if (!environment.production) {
      (window as any).logout = () => this.signOut();
    }
  }

  /* ================================================================== */
  /*  Session bootstrap (called from APP_INITIALIZER)                   */
  /* ================================================================== */
  verifyActiveSession = (): Promise<void> => {
    return firstValueFrom(this.afAuth.authState)
      .then((user) => {
        if (user) {
          this._isLoggedIn = true;
          this._accessToken = (user as any).accessToken ?? null;
          this._user$.next(user);
        }
      })
      .catch(() => {
        this.toastr.error('There is a problem with the server.', 'Please try again later');
        return; // ← returns undefined (void)
      });
  };

  /* ================================================================== */
  /*  Sign-in / Sign-out                                                */
  /* ================================================================== */
  signIn(email: string, password: string): Promise<boolean> {
    return this.afAuth
      .signInWithEmailAndPassword(email, password)
      .then((result) => {
        this._isLoggedIn = true;

        if (result.user) {
          this._user$.next(result.user); // publish
          return result.user.getIdToken(); // Promise<string>
        }

        /* always return a Promise<string | null> */
        return Promise.resolve<string | null>(null); // ✅ matches
      })
      .then((token) => {
        if (token) {
          this._accessToken = token;
        }
        return true;
      })
      .catch((err) => {
        this._isLoggedIn = false;
        return Promise.reject(err);
      });
  }

  signOut(): Promise<void> {
    return this.afAuth.signOut().then(() => {
      this._isLoggedIn = false;
      this._accessToken = null;
      this._user$.next(null); // clear
      this.router.navigate(['login']);
    });
  }

  /* ================================================================== */
  /*  Convenience getters                                               */
  /* ================================================================== */

  /** Current firebase.User (or null) */
  get currentUser(): firebase.User | null {
    return this._user$.value;
  }

  /** Display name (null if not set) */
  get displayName(): string | null {
    return this.currentUser?.displayName ?? null;
  }

  /** Quick auth flag */
  get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  /** Retrieve a fresh ID token (Promise) */
  get accessToken(): Promise<string> | null {
    return new Promise(async (resolve, reject) => {
      const token = await (await this.afAuth.currentUser)?.getIdToken();
      if (token) {
        resolve(token);
      } else {
        reject('ID token not present');
      }
    });
  }

  /* ================================================================== */
  /*  Optional helper: update the user’s display name                   */
  /* ================================================================== */
  async setDisplayName(name: string): Promise<void> {
    if (!this.currentUser) {
      throw new Error('No user signed in');
    }
    await this.currentUser.updateProfile({ displayName: name });
    this._user$.next({ ...this.currentUser, displayName: name } as firebase.User);
  }
}
